import { useCallback, useEffect, useState } from "react";
import { getCoachFunnel } from "../apicalls/coachMatchRequests";

// Coach funnel: players reaching out to a coach (first contact per new player–coach pair, by
// booking request or "Message coach" tap) and how many then book. Definitions live in the API
// (ttp-api src/services/coach_funnel.js); this page only shows the numbers.

const DAY = 86400000;
const toDateInput = (date) => date.toISOString().slice(0, 10);
const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : "-");
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");
const formatPct = (value) => (value === null || value === undefined ? "-" : `${value}%`);
const channelLabel = (channel) => (channel === "message" ? "Message tap" : "Booking request");
const waitingLabel = (hours) => (hours >= 48 ? `${Math.floor(hours / 24)} days` : `${hours} h`);

const EMPTY = { summary: null, weekly: [], by_coach: [], stuck_requests: [], recent_contacts: [], definitions: null };

function Stat({ label, value, sub }) {
  return <div className="card mr-3 mb-3" style={{ minWidth: 180 }}><div className="card-body py-3">
    <div className="text-muted small">{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
    {sub ? <div className="text-muted small">{sub}</div> : null}
  </div></div>;
}

function CoachFunnel({ token }) {
  const today = new Date();
  const [range, setRange] = useState({ from: toDateInput(new Date(today.getTime() - 56 * DAY)), to: toDateInput(today), exclude: "" });
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      // `to` is inclusive on screen, so ask for up to the start of the next day.
      const to = new Date(`${range.to}T00:00:00`);
      setData({ ...EMPTY, ...(await getCoachFunnel({
        from: new Date(`${range.from}T00:00:00`).toISOString(),
        to: new Date(to.getTime() + DAY).toISOString(),
        exclude: range.exclude.replace(/\s+/g, ""),
      }, token)) });
    } catch (err) {
      setError(err.message || "Could not load the coach funnel");
    } finally {
      setLoading(false);
    }
  }, [range, token]);
  useEffect(() => { load(); }, [load]);

  const summary = data.summary;
  const channels = summary?.by_channel || {};
  const maxWeek = Math.max(1, ...data.weekly.map((week) => week.reached_out));

  return <section>
    {error ? <div className="alert alert-danger">{error}</div> : null}
    <div className="d-flex flex-wrap align-items-end mb-3">
      <label className="mr-2 mb-2">From<input className="form-control" type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} /></label>
      <label className="mr-2 mb-2">To<input className="form-control" type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} /></label>
      <label className="mr-2 mb-2" style={{ maxWidth: 240 }}>Exclude user IDs<input className="form-control" placeholder="e.g. 6, 99" value={range.exclude} onChange={(e) => setRange({ ...range, exclude: e.target.value })} /></label>
      {loading ? <span className="mb-3 text-muted">Loading…</span> : null}
    </div>

    {summary ? <>
      <div className="d-flex flex-wrap">
        <Stat label="Players who reached out" value={summary.reached_out} sub="new player–coach pairs" />
        <Stat label="Booked a lesson" value={summary.booked} sub={`within ${data.definitions?.book_within_days ?? 14} days`} />
        <Stat label="Conversion" value={formatPct(summary.conversion_pct)} />
        <Stat label="Stuck requests" value={data.stuck_requests.length} sub={`pending over ${data.definitions?.stuck_after_hours ?? 48} h`} />
      </div>
      <p className="text-muted">
        By booking request: {channels.booking_request?.reached_out ?? 0} reached out → {channels.booking_request?.booked ?? 0} booked ({formatPct(channels.booking_request?.conversion_pct)}).{" "}
        By message: {channels.message?.reached_out ?? 0} → {channels.message?.booked ?? 0} ({formatPct(channels.message?.conversion_pct)}).
        {" "}Messages are taps on "Message coach", counted from when tracking started; whether the text was sent isn't known.
      </p>
    </> : null}

    <h5 className="mt-4">By week</h5>
    <div className="table-responsive"><table className="table table-sm"><thead><tr><th>Week of</th><th>Reached out</th><th>Booked</th><th>Conversion</th><th style={{ width: "35%" }} /></tr></thead><tbody>
      {data.weekly.map((week) => <tr key={week.week_start}><td>{formatDate(`${week.week_start}T12:00:00`)}</td><td>{week.reached_out}</td><td>{week.booked}</td><td>{formatPct(week.conversion_pct)}</td><td>
        <div style={{ background: "#e9ecef", height: 10, borderRadius: 5, position: "relative" }} aria-hidden="true">
          <div style={{ width: `${(week.reached_out / maxWeek) * 100}%`, background: "#c7b8f5", height: 10, borderRadius: 5 }} />
          <div style={{ width: `${(week.booked / maxWeek) * 100}%`, background: "#7c3aed", height: 10, borderRadius: 5, position: "absolute", top: 0, left: 0 }} />
        </div>
      </td></tr>)}
      {!data.weekly.length ? <tr><td colSpan="5">No players reached out in this period.</td></tr> : null}
    </tbody></table></div>

    <h5 className="mt-4">Stuck requests</h5>
    <p className="text-muted small">Lesson requests a player made that the coach hasn't confirmed after {data.definitions?.stuck_after_hours ?? 48} hours.</p>
    <div className="table-responsive"><table className="table table-sm"><thead><tr><th>Requested</th><th>Waiting</th><th>Player</th><th>Coach</th><th>Lesson</th></tr></thead><tbody>
      {data.stuck_requests.map((row) => <tr key={row.lesson_id}><td>{formatDateTime(row.requested_at)}</td><td>{waitingLabel(row.hours_waiting)}</td><td>{row.player_name || `#${row.player_id}`}</td><td>{row.coach_name || `#${row.coach_id}`}</td><td>{formatDateTime(row.lesson_start_at)}</td></tr>)}
      {!data.stuck_requests.length ? <tr><td colSpan="5">No stuck requests.</td></tr> : null}
    </tbody></table></div>

    <h5 className="mt-4">By coach</h5>
    <div className="table-responsive"><table className="table table-sm"><thead><tr><th>Coach</th><th>Reached out</th><th>Booked</th><th>Conversion</th></tr></thead><tbody>
      {data.by_coach.map((row) => <tr key={row.coach_id}><td>{row.coach_name || `#${row.coach_id}`}</td><td>{row.reached_out}</td><td>{row.booked}</td><td>{formatPct(row.conversion_pct)}</td></tr>)}
      {!data.by_coach.length ? <tr><td colSpan="4">No coaches contacted in this period.</td></tr> : null}
    </tbody></table></div>

    <h5 className="mt-4">Contacts</h5>
    <p className="text-muted small">The first contact for each new player–coach pair behind the numbers above (latest 200).</p>
    <div className="table-responsive"><table className="table table-sm"><thead><tr><th>First contact</th><th>Player</th><th>Coach</th><th>How</th><th>Booked</th><th>Days to book</th></tr></thead><tbody>
      {data.recent_contacts.map((row) => <tr key={`${row.player_id}-${row.coach_id}`}><td>{formatDateTime(row.first_contact_at)}</td><td>{row.player_name || `#${row.player_id}`}</td><td>{row.coach_name || `#${row.coach_id}`}</td><td>{channelLabel(row.channel)}</td><td>{row.booked_at ? formatDate(row.booked_at) : "Not yet"}</td><td>{row.days_to_book ?? "-"}</td></tr>)}
      {!data.recent_contacts.length ? <tr><td colSpan="6">No contacts in this period.</td></tr> : null}
    </tbody></table></div>

    {data.definitions ? <p className="text-muted small mt-3">
      Reached out = first booking request or "Message coach" tap per player–coach pair, leaving out players who already had a confirmed lesson with that coach. Booked = a confirmed private lesson with that coach within {data.definitions.book_within_days} days. Excludes self-bookings, deleted players{data.definitions.excluded_user_ids?.length ? ` and user IDs ${data.definitions.excluded_user_ids.join(", ")}` : ""}. Private lessons only.
    </p> : null}
  </section>;
}

export default CoachFunnel;
